/**
 * useActionHints Hook
 * 
 * Provides action hints based on current game state.
 * Integrates with game settings to respect actionHintsEnabled toggle.
 */

import { useMemo } from 'react';
import { GameState } from '../types';
import { getValidActionHints, ActionHint, HintProviderResult, getHintForElement } from '../services/aiHintProvider';
import { useGameSettings } from './useGameSettings';
import { AIAction } from '../services/ai';

interface UseActionHintsResult {
    hints: ActionHint[];
    bestAction: AIAction | null;
    isEnabled: boolean;
    getHint: (elementId: string) => ActionHint | undefined;
    hasHintFor: (elementId: string) => boolean;
}

/**
 * Hook to get action hints for the current player
 * 
 * @param gameState - Current game state
 * @param playerId - ID of the player to get hints for
 * @returns Object containing hints array and helper functions
 */
export function useActionHints(
    gameState: GameState | null,
    playerId: number
): UseActionHintsResult {
    const { settings } = useGameSettings();
    const isEnabled = settings.actionHintsEnabled;

    const hintsResult = useMemo<HintProviderResult>(() => {
        if (!gameState || !isEnabled) {
            return { hints: [], bestAction: null };
        }

        try {
            return getValidActionHints(gameState, playerId);
        } catch (e) {
            console.warn('[useActionHints] Error getting hints:', e);
            return { hints: [], bestAction: null };
        }
    }, [gameState, playerId, isEnabled]);

    const getHint = (elementId: string): ActionHint | undefined => {
        if (!isEnabled) return undefined;
        return getHintForElement(hintsResult.hints, elementId);
    };

    const hasHintFor = (elementId: string): boolean => {
        if (!isEnabled) return false;
        return hintsResult.hints.some(h => h.elementId === elementId);
    };

    return {
        hints: isEnabled ? hintsResult.hints : [],
        bestAction: isEnabled ? hintsResult.bestAction : null,
        isEnabled,
        getHint,
        hasHintFor
    };
}

export default useActionHints;
